import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { bookings } from '@repletoryreach/sdk-api';
import { Button, Group, Stack, Text, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import { BOOKINGS_KEY, bookingErrorText, useDateLocale } from '../lib/data';
import { addDays, formatInZone, formatLocalDate, localDateOf, localTimeOf, todayIn } from '../lib/time';

/** What was picked: an appointment start, a call-out window or a class session. */
export interface AvailabilityPick {
  starts_at: string;
  ends_at: string;
  /** People free at that time (appointments only). */
  staff_ids?: string[];
  session_id?: string;
  staff_id?: string | null;
}

interface Option extends AvailabilityPick {
  key: string;
  date: string;
  label: string;
  hint?: string;
}

const DAYS_SHOWN = 7;

/**
 * Days, then times. One week of the service's availability at a time, asked
 * of the service so the console offers exactly what a client would be offered
 * (the same engine answers flows and the public page).
 */
export function AvailabilityPicker({
  service, staffId, tz, value, onChange, initialDate, initialTime, refreshKey
}: {
  service: bookings.Service;
  /** Only this person's times. */
  staffId?: string;
  tz: string;
  value: AvailabilityPick | null;
  onChange: (pick: AvailabilityPick | null) => void;
  /** Open on this local date's week. */
  initialDate?: string;
  /** Choose this local `HH:mm` on `initialDate` when it is free. */
  initialTime?: string;
  /** Change it to ask again (after a time was taken). */
  refreshKey?: number;
}) {
  const t = useT();
  const locale = useDateLocale();
  const today = todayIn(tz);
  const [from, setFrom] = useState(() => (initialDate && initialDate > today ? initialDate : today));
  const [day, setDay] = useState<string | null>(initialDate ?? null);
  const to = addDays(from, DAYS_SHOWN - 1);

  const q = useQuery({
    queryKey: [BOOKINGS_KEY, 'availability', service.id, staffId ?? '', from, to, refreshKey ?? 0],
    queryFn: () => bookings.getAvailability({ service_id: service.id, from, to, staff_id: staffId || undefined }),
    staleTime: 15_000
  });
  const avail = q.data?.data;
  const zone = avail?.timezone || tz;

  const options = useMemo<Option[]>(() => {
    if (!avail) return [];
    const time = (iso: string) => localTimeOf(iso, zone);
    if (avail.kind === 'class') {
      return (avail.sessions ?? []).map((s) => ({
        key: s.id,
        date: localDateOf(s.starts_at, zone),
        starts_at: s.starts_at,
        ends_at: s.ends_at,
        session_id: s.id,
        staff_id: s.staff_id,
        label: time(s.starts_at),
        hint: t('bookings.picker.placesLeft', { count: s.places_left })
      }));
    }
    if (avail.kind === 'callout') {
      return (avail.windows ?? []).map((w) => ({
        key: w.starts_at,
        date: localDateOf(w.starts_at, zone),
        starts_at: w.starts_at,
        ends_at: w.ends_at,
        label: t('bookings.picker.window', { from: time(w.starts_at), to: time(w.ends_at) }),
        hint: t('bookings.picker.placesLeft', { count: w.places_left })
      }));
    }
    return (avail.slots ?? []).map((s) => ({
      key: s.starts_at,
      date: localDateOf(s.starts_at, zone),
      starts_at: s.starts_at,
      ends_at: s.ends_at,
      staff_ids: s.staff_ids,
      label: time(s.starts_at)
    }));
  }, [avail, zone, t]);

  const byDay = useMemo(() => {
    const m = new Map<string, Option[]>();
    for (const o of options) {
      const list = m.get(o.date) ?? [];
      list.push(o);
      m.set(o.date, list);
    }
    return m;
  }, [options]);

  const days = Array.from({ length: DAYS_SHOWN }, (_, i) => addDays(from, i));

  // Land on the first day with something free when the chosen day has nothing.
  useEffect(() => {
    if (!avail) return;
    if (day && byDay.has(day)) return;
    const first = days.find((d) => byDay.has(d)) ?? null;
    setDay(first);
  }, [avail]); // eslint-disable-line react-hooks/exhaustive-deps

  // The time clicked on the calendar, chosen for them once, when it is free.
  const [usedInitial, setUsedInitial] = useState(false);
  useEffect(() => {
    if (usedInitial || !avail || !initialDate || !initialTime || value) return;
    setUsedInitial(true);
    const hit = (byDay.get(initialDate) ?? []).find((o) => localTimeOf(o.starts_at, zone) === initialTime);
    if (hit) onChange({ starts_at: hit.starts_at, ends_at: hit.ends_at, staff_ids: hit.staff_ids, session_id: hit.session_id, staff_id: hit.staff_id });
  }, [avail]); // eslint-disable-line react-hooks/exhaustive-deps

  const times = day ? byDay.get(day) ?? [] : [];
  const selectedKey = value ? (value.session_id ?? value.starts_at) : null;

  return (
    <Stack gap="sm" data-testid="availability-picker">
      <Group gap="xs" justify="space-between" wrap="wrap">
        <Text role="inputLabel">{t('bookings.picker.day')}</Text>
        <Group gap="xs">
          <Button
            size="xs"
            variant="default"
            disabled={from <= today}
            onClick={() => { setFrom(addDays(from, -DAYS_SHOWN) < today ? today : addDays(from, -DAYS_SHOWN)); onChange(null); }}
          >
            {t('bookings.picker.earlier')}
          </Button>
          <Button size="xs" variant="default" onClick={() => { setFrom(addDays(from, DAYS_SHOWN)); onChange(null); }}>
            {t('bookings.picker.later')}
          </Button>
        </Group>
      </Group>

      {q.error && (
        <Alert color="error" role="alert">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{bookingErrorText(t, q.error)}</span>
            <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      <div className="rr-bookings__days" role="listbox" aria-label={t('bookings.picker.day')}>
        {days.map((d) => {
          const count = byDay.get(d)?.length ?? 0;
          const active = d === day;
          return (
            <button
              key={d}
              type="button"
              role="option"
              aria-selected={active}
              disabled={!q.isPending && count === 0}
              className={`rr-bookings__day${active ? ' is-active' : ''}`}
              onClick={() => { setDay(d); onChange(null); }}
            >
              <span className="rr-bookings__day-name">{formatLocalDate(d, locale, { weekday: 'short' })}</span>
              <span className="rr-bookings__day-num">{formatLocalDate(d, locale, { day: 'numeric', month: 'short' })}</span>
            </button>
          );
        })}
      </div>

      <Text role="inputLabel">
        {service.kind === 'class' ? t('bookings.picker.session') : service.kind === 'callout' ? t('bookings.picker.windowLabel') : t('bookings.picker.time')}
      </Text>
      {q.isPending ? (
        <Skeleton height={36} />
      ) : times.length === 0 ? (
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {options.length === 0 ? t('bookings.picker.noneThisWeek') : t('bookings.picker.noneThisDay')}
        </Text>
      ) : (
        <div className="rr-bookings__times" role="listbox" aria-label={t('bookings.picker.time')}>
          {times.map((o) => {
            const active = selectedKey === (o.session_id ?? o.starts_at);
            return (
              <button
                key={o.key}
                type="button"
                role="option"
                aria-selected={active}
                className={`rr-bookings__time${active ? ' is-active' : ''}`}
                onClick={() => onChange({
                  starts_at: o.starts_at, ends_at: o.ends_at, staff_ids: o.staff_ids, session_id: o.session_id, staff_id: o.staff_id
                })}
              >
                <span>{o.label}</span>
                {o.hint && <span className="rr-bookings__time-hint">{o.hint}</span>}
              </button>
            );
          })}
        </div>
      )}
      {value && (
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('bookings.picker.chosen', {
            when: formatInZone(value.starts_at, zone, locale, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
          })}
        </Text>
      )}
    </Stack>
  );
}
